import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Grid,
  Card,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Checkbox,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import { ArrowBack, Save, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { FiUser, FiLock, FiInfo, FiEye, FiEyeOff } from 'react-icons/fi';
import { authAPI } from '../services/api';
import { colors } from '../theme/modernTheme';
import { showSuccess, showError } from '../utils/toast';
import { useAuth } from '../context/AuthContext';
import { MODULE_DEFINITIONS, DEFAULT_PERMISSIONS_BY_ROLE } from '../utils/modulePermissions';

// Section Header Component for consistent styling
const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <Box sx={{ mb: 3 }}>
    <Box display="flex" alignItems="center" gap={1.5} mb={subtitle ? 0.5 : 0}>
      {Icon && (
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '4px',
            backgroundColor: `${colors.primary.main}10`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon style={{ color: colors.primary.main, fontSize: 18 }} />
        </Box>
      )}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: colors.text.primary,
          fontSize: '1rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        {title}
      </Typography>
    </Box>
    {subtitle && (
      <Typography
        variant="body2"
        sx={{
          color: colors.text.secondary,
          ml: Icon ? 6 : 0,
          fontSize: '0.8rem',
        }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

// Module Permission Card Component
const ModulePermissionCard = ({ module, isChecked, onChange, disabled }) => (
  <Box
    onClick={disabled ? undefined : onChange}
    sx={{
      p: 2,
      borderRadius: '4px',
      border: `1px solid ${isChecked ? colors.primary.main : colors.border.main}`,
      backgroundColor: isChecked ? `${colors.primary.main}05` : '#FFFFFF',
      cursor: disabled ? 'default' : 'pointer',
      transition: 'all 0.2s ease',
      opacity: disabled ? 0.6 : 1,
      '&:hover': disabled
        ? {}
        : {
            borderColor: colors.primary.main,
            backgroundColor: `${colors.primary.main}08`,
          },
    }}
  >
    <Box display="flex" alignItems="flex-start" gap={1.5}>
      <Checkbox
        checked={isChecked}
        disabled={disabled}
        sx={{
          p: 0,
          pointerEvents: 'none',
          color: colors.border.main,
          '&.Mui-checked': {
            color: colors.primary.main,
          },
        }}
      />
      <Box flex={1}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: colors.text.primary,
            fontSize: '0.85rem',
            mb: 0.5,
          }}
        >
          {module.label}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: colors.text.secondary,
            fontSize: '0.75rem',
            lineHeight: 1.4,
            display: 'block',
          }}
        >
          {module.description}
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default function UserFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user: currentUser } = useAuth();

  const initialMode = id && id !== 'new' && searchParams.get('mode') !== 'edit' ? 'view' : 'edit';
  const [mode, setMode] = useState(initialMode);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'STAFF',
    is_active: true,
    module_permissions: [],
  });
  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isViewMode = mode === 'view';
  const isNewUser = id === 'new' || !id;
  const canEdit = currentUser?.role === 'ADMIN';

  useEffect(() => {
    if (id && id !== 'new') {
      loadUser();
    }
  }, [id]);

  const loadUser = async () => {
    setLoadingUser(true);
    try {
      const response = await authAPI.getUserById(id);
      const user = response.data;
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        is_active: user.is_active,
        module_permissions: user.module_permissions || [],
      });
    } catch (error) {
      const errorMessage = error.error?.message || error.message || 'Failed to load user';
      showError(errorMessage);
      navigate('/users');
    } finally {
      setLoadingUser(false);
    }
  };

  const handleEditClick = () => {
    setMode('edit');
    navigate(`/users/${id}?mode=edit`, { replace: true });
  };

  const handleCancelEdit = () => {
    if (id && id !== 'new') {
      setMode('view');
      navigate(`/users/${id}`, { replace: true });
      loadUser();
    } else {
      navigate('/users');
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await authAPI.deleteUser(id);
      showSuccess('User deleted successfully');
      navigate('/users');
    } catch (error) {
      const errorMessage = error.error?.message || error.message || 'Failed to delete user';
      showError(errorMessage);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    let newValue;
    if (name === 'is_active') {
      newValue = checked;
    } else if (type === 'checkbox') {
      newValue = checked;
    } else {
      newValue = value;
    }

    if (name === 'role' && value === 'ADMIN') {
      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
        module_permissions: DEFAULT_PERMISSIONS_BY_ROLE.ADMIN,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    }

    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleModulePermissionChange = (moduleKey) => {
    setFormData((prev) => {
      const currentPermissions = prev.module_permissions || [];
      const newPermissions = currentPermissions.includes(moduleKey)
        ? currentPermissions.filter((p) => p !== moduleKey)
        : [...currentPermissions, moduleKey];
      return {
        ...prev,
        module_permissions: newPermissions,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFieldErrors({});

    const errors = {};

    if (!formData.name?.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters long';
    }

    if (!formData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please provide a valid email address';
    }

    if (isNewUser && !formData.password?.trim()) {
      errors.password = 'Password is required for new users';
    } else if (formData.password?.trim() && formData.password.trim().length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setLoading(false);
      return;
    }

    try {
      const submitData = { ...formData };
      // Only include password if it's provided and not empty/whitespace
      if (!submitData.password || !submitData.password.trim()) {
        delete submitData.password;
      } else {
        // Trim the password before sending
        submitData.password = submitData.password.trim();
      }

      if (!submitData.module_permissions) {
        submitData.module_permissions = [];
      }

      if (isNewUser) {
        await authAPI.createUser(submitData);
        showSuccess('User created successfully');
        navigate('/users');
      } else {
        await authAPI.updateUser(id, submitData);
        showSuccess('User updated successfully');
        setMode('view');
        navigate(`/users/${id}`, { replace: true });
        loadUser();
      }
    } catch (err) {
      if (err.error?.code === 'VALIDATION_ERROR') {
        const fieldErrors = err.error.details?.field_errors || {};
        setFieldErrors(fieldErrors);
      } else if (err.error?.code === 'EMAIL_ALREADY_EXISTS') {
        setFieldErrors({
          email: 'A user with this email address already exists',
        });
      } else {
        showError(err.error?.message || err.message || 'Failed to save user');
      }
    } finally {
      setLoading(false);
    }
  };

  const getDisplayValue = (value) => {
    if (!value && value !== false) return '—';
    return value;
  };

  const getRoleLabel = (role) => {
    const labels = {
      ADMIN: 'Administrator',
      STAFF: 'Staff Member',
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'ADMIN':
        return {
          bg: `${colors.error.main}15`,
          color: colors.error.main,
        };
      case 'STAFF':
        return {
          bg: `${colors.primary.main}15`,
          color: colors.primary.main,
        };
      default:
        return {
          bg: colors.action.hover,
          color: colors.text.secondary,
        };
    }
  };

  const getRoleDescription = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'Full system access including user management';
      case 'STAFF':
        return 'Access based on assigned module permissions';
      default:
        return '';
    }
  };

  const getModulePermissionLabel = (moduleKey) => {
    const module = MODULE_DEFINITIONS.find((m) => m.key === moduleKey);
    return module ? module.label : moduleKey;
  };

  if (loadingUser) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pb: 4 }}>
      {isViewMode ? (
        // View Mode Layout
        <Box>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <IconButton
                onClick={() => navigate('/users')}
                size="small"
                sx={{
                  '&:hover': { color: 'rgba(0, 0, 0, 1)' },
                }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#000000',
                }}
              >
                User Details
              </Typography>
            </Box>
            {canEdit && (
              <Box display="flex" gap={1}>
                <Tooltip title="Edit User" arrow>
                  <IconButton
                    onClick={handleEditClick}
                    sx={{
                      color: colors.text.primary,
                      '&:hover': {
                        color: colors.primary.main,
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete User" arrow>
                  <IconButton
                    onClick={handleDeleteClick}
                    sx={{
                      color: colors.text.primary,
                      '&:hover': {
                        color: colors.error.main,
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          {/* Content Cards */}
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: '4px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  p: 3,
                  height: '100%',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: colors.primary.main,
                    mb: 2,
                    pb: 1,
                    borderBottom: `2px solid ${colors.primary.main}`,
                  }}
                >
                  Basic Information
                </Typography>

                <Box display="flex" flexDirection="column" gap={2.5}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Full Name
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.text.primary,
                        fontWeight: 500,
                        mt: 0.5,
                      }}
                    >
                      {getDisplayValue(formData.name)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Email Address
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.text.primary,
                        fontWeight: 500,
                        mt: 0.5,
                      }}
                    >
                      {getDisplayValue(formData.email)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Account Status
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: formData.is_active
                            ? colors.success.main
                            : colors.error.main,
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 500,
                        }}
                      >
                        {formData.is_active ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Role & Permissions */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  borderRadius: '4px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  p: 3,
                  height: '100%',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: colors.primary.main,
                    mb: 2,
                    pb: 1,
                    borderBottom: `2px solid ${colors.primary.main}`,
                  }}
                >
                  Role & Permissions
                </Typography>

                <Box display="flex" flexDirection="column" gap={2.5}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                      }}
                    >
                      User Role
                    </Typography>
                    <Box mt={0.5}>
                      <Chip
                        label={getRoleLabel(formData.role)}
                        size="small"
                        sx={{
                          borderRadius: '4px',
                          backgroundColor: getRoleColor(formData.role).bg,
                          color: getRoleColor(formData.role).color,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.text.secondary,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Module Permissions
                    </Typography>
                    {formData.role === 'ADMIN' ? (
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.text.secondary,
                          mt: 0.5,
                          fontStyle: 'italic',
                        }}
                      >
                        Administrators have access to all modules
                      </Typography>
                    ) : formData.module_permissions && formData.module_permissions.length > 0 ? (
                      <Box display="flex" flexWrap="wrap" gap={0.75} mt={1}>
                        {formData.module_permissions.map((perm) => (
                          <Chip
                            key={perm}
                            label={getModulePermissionLabel(perm)}
                            size="small"
                            sx={{
                              borderRadius: '4px',
                              backgroundColor: `${colors.primary.main}12`,
                              color: colors.primary.main,
                              fontWeight: 500,
                              fontSize: '0.7rem',
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.text.secondary,
                          mt: 0.5,
                          fontStyle: 'italic',
                        }}
                      >
                        No module permissions assigned
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
      ) : (
        // Edit Mode Layout
        <Box>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <IconButton
                onClick={() => navigate('/users')}
                size="small"
                sx={{
                  '&:hover': { color: 'rgba(0, 0, 0, 1)' },
                }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#000000',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {isNewUser ? 'Add New User' : 'Edit User'}
              </Typography>
            </Box>
            <Box display="flex" gap={1.5}>
              <Button
                variant="contained"
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Save fontSize="small" />
                  )
                }
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  borderRadius: '4px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5,
                  backgroundColor: colors.primary.main,
                  '&:hover': {
                    backgroundColor: colors.primary.dark,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: colors.action.disabledBackground,
                    color: colors.text.disabled,
                  },
                }}
              >
                {isNewUser ? 'Create User' : 'Save Changes'}
              </Button>
            </Box>
          </Box>

          {/* Form Container */}
          <Paper
            sx={{
              borderRadius: '4px !important',
              boxShadow: 'none',
              border: `1px solid ${colors.border.main}`,
              overflow: 'hidden',
            }}
          >
            <form onSubmit={handleSubmit}>
              {/* Basic Information Section */}
              <Box
                sx={{
                  p: 3,
                  backgroundColor: '#FFFFFF',
                }}
              >
                <SectionHeader
                  icon={FiUser}
                  title="Basic Information"
                  subtitle="Enter the user's personal details and role"
                />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      autoFocus
                      placeholder="Enter full name"
                      error={!!fieldErrors.name}
                      helperText={fieldErrors.name || ''}
                      FormHelperTextProps={{
                        sx: { marginLeft: 0 },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '4px',
                          backgroundColor: '#FFFFFF',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="Enter email address"
                      error={!!fieldErrors.email}
                      helperText={fieldErrors.email || ''}
                      FormHelperTextProps={{
                        sx: { marginLeft: 0 },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '4px',
                          backgroundColor: '#FFFFFF',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={isNewUser ? 'Password' : 'New Password (Optional)'}
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      required={isNewUser}
                      placeholder={
                        isNewUser ? 'Enter password' : 'Leave blank to keep current password'
                      }
                      error={!!fieldErrors.password}
                      helperText={
                        fieldErrors.password ||
                        (isNewUser
                          ? 'Minimum 6 characters'
                          : 'Leave blank to keep current password. Minimum 6 characters if changing.')
                      }
                      FormHelperTextProps={{
                        sx: { marginLeft: 0 },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              size="small"
                              sx={{ color: colors.text.secondary }}
                            >
                              {showPassword ? <FiEye size={18} /> : <FiEyeOff size={18} />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '4px',
                          backgroundColor: '#FFFFFF',
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="User Role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      required
                      error={!!fieldErrors.role}
                      helperText={fieldErrors.role || getRoleDescription(formData.role)}
                      FormHelperTextProps={{
                        sx: { marginLeft: 0 },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '4px',
                          backgroundColor: '#FFFFFF',
                        },
                      }}
                    >
                      <MenuItem value="ADMIN">Administrator</MenuItem>
                      <MenuItem value="STAFF">Staff Member</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Module Permissions Section */}
              <Box sx={{ p: 3, backgroundColor: '#FFFFFF' }}>
                <SectionHeader
                  icon={FiLock}
                  title="Module Permissions"
                  subtitle="Select the modules this user can access. Users will only see menu items and features for their assigned modules."
                />

                {formData.role === 'ADMIN' ? (
                  <Alert
                    severity="info"
                    icon={<FiInfo style={{ fontSize: 20 }} />}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: `${colors.primary.main}08`,
                      border: `1px solid ${colors.primary.main}20`,
                      '& .MuiAlert-icon': {
                        color: colors.primary.main,
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Administrators automatically have access to all modules and features.
                    </Typography>
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {MODULE_DEFINITIONS.map((module) => (
                      <Grid item xs={12} sm={6} md={4} key={module.key}>
                        <ModulePermissionCard
                          module={module}
                          isChecked={(formData.module_permissions || []).includes(module.key)}
                          onChange={() => handleModulePermissionChange(module.key)}
                          disabled={formData.role === 'ADMIN'}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>

              <Divider />
            </form>
          </Paper>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this user? This action will deactivate the user account.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleting}
            sx={{
              textTransform: 'none',
              color: colors.text.secondary,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              textTransform: 'none',
              backgroundColor: colors.error.main,
              '&:hover': {
                backgroundColor: colors.error.dark,
              },
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
